import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';

export const Route = createFileRoute('/sso/admin/portal')({
  component: AdminPortalPage,
});

// Module-level cache: Stytch warns if createStytchB2BClient is called multiple times.
// Cache by public token so HMR re-mounts don't trigger duplicate client warnings.
// oxlint-disable-next-line typescript/no-explicit-any -- Stytch client type comes from dynamic import, not available at module level
let cachedClient: { token: string; client: any } | null = null;

function AdminPortalPage() {
  'use no memo'; // Dynamic imports (Stytch SDK) prevent React Compiler optimization
  /* oxlint-disable react/react-compiler */
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [PortalComponent, setPortalComponent] = useState<React.ComponentType | null>(null);

  useEffect(() => {
    let cancelled = false;
    const initPortal = async () => {
      try {
        // Read the public token from localStorage (stored by the /_auth/sso/admin page)
        const publicToken = window.localStorage.getItem('stytch_public_token');
        if (!publicToken) {
          if (!cancelled) {
            setError('SSO session not found. Please start from the SSO admin page.');
            setLoading(false);
          }
          return;
        }

        // Dynamically import Stytch React components
        const { StytchB2BProvider, createStytchB2BClient } = await import('@stytch/react/b2b');
        const { AdminPortalSSO, AdminPortalSCIM } = await import('@stytch/react/b2b/adminPortal');

        const stytchClient = (() => {
          if (cachedClient && cachedClient.token === publicToken) {
            return cachedClient.client;
          }
          const client = createStytchB2BClient(publicToken);
          cachedClient = { token: publicToken, client };
          return client;
        })();

        // Check if we have an active session
        const session = stytchClient.session.getSync();
        if (!session) {
          if (!cancelled) {
            setError('Your session has expired. Please request a new magic link.');
            setLoading(false);
          }
          return;
        }

        if (!cancelled) {
          // Storing a React component in useState is intentional here:
          // The Stytch SDK requires dynamic import (code-split) and the StytchB2BProvider
          // needs the client instance from the same dynamic import scope. Defining Portal
          // inside the effect captures the stytchClient in closure. Since deps are [],
          // this only runs once per mount — no recreation on re-renders.
          // The () => Portal wrapper is required because setState(() => fn) treats functions
          // as updaters; wrapping prevents React from calling Portal as an updater.
          // oxlint-disable-next-line react/no-unstable-nested-components -- intentional: Portal captures dynamically-imported stytchClient from closure scope; cannot be extracted to module level
          const Portal = () => (
            <StytchB2BProvider stytch={stytchClient}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                SSO Configuration
              </Typography>
              <AdminPortalSSO
                config={{
                  testLoginRedirectURL: `${window.location.origin}/sso/callback`,
                  testSignupRedirectURL: `${window.location.origin}/sso/callback`,
                }}
              />
              <Box sx={{ mt: 4 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  SCIM Provisioning
                </Typography>
                <AdminPortalSCIM />
              </Box>
            </StytchB2BProvider>
          );
          setPortalComponent(() => Portal);
          setLoading(false);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load SSO portal';
          setError(message);
          setLoading(false);
        }
      }
    };

    void initPortal();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>Loading SSO Configuration...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Typography color="error" sx={{ mb: 2, textAlign: 'center' }}>
          {error}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            void navigate({ to: '/' });
          }}
        >
          Return to Dashboard
        </Button>
      </Box>
    );
  }

  if (PortalComponent) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Button
          variant="outlined"
          size="small"
          sx={{ mb: 2 }}
          onClick={() => {
            void navigate({ to: '/grain_bin/dashboard' });
          }}
        >
          ← Return to Dashboard
        </Button>
        <PortalComponent />
      </Box>
    );
  }

  return null;
}
